/**
 * Deploy-time configuration, read when the code RUNS rather than when it is
 * built.
 *
 * `next build` inlines every `process.env.NEXT_PUBLIC_*` it can see — into the
 * server bundles as well as the browser ones — so a value read that way is
 * frozen into the image. That is what forced a separate stage build and prod
 * build of the same commit. Atlas now builds one image per commit and promotes
 * it unchanged from stage to prod, so every environment-specific value must
 * come from the container's environment instead:
 *
 * - **server** — `process.env[key]` with a computed key, which Next does not
 *   inline (node_modules/next/dist/docs/01-app/02-guides/environment-variables.md).
 * - **browser** — `/api/public-env` serves the allowlisted values as a script,
 *   and the root layout loads it `beforeInteractive`, ahead of any app code.
 *
 * A literal `process.env.NEXT_PUBLIC_*` read anywhere in `src/` is a lint error
 * for that reason: in a production image it evaluates to whatever the build
 * machine had, which is nothing.
 *
 * Only values safe to hand to every visitor belong in `PUBLIC_ENV_KEYS` — the
 * whole set is served unauthenticated. Dependency-free on purpose: `proxy.ts`
 * and `instrumentation-client.ts` both reach it.
 */

export const PUBLIC_ENV_KEYS = [
  'NEXT_PUBLIC_AUTH_URL',
  'NEXT_PUBLIC_APP_VERSION',
  'NEXT_PUBLIC_GOOGLE_ANALYTICS_ID',
  'NEXT_PUBLIC_GOOGLE_MAPS_API_KEY',
  'NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID',
  'NEXT_PUBLIC_MICROSOFT_CLIENT_ID',
  'NEXT_PUBLIC_MICROSOFT_TENANT_ID',
  'NEXT_PUBLIC_SERVICENOW_INSTANCE_URL',
  'NEXT_PUBLIC_VOICE_INBOUND_NUMBER',
  'NEXT_PUBLIC_VAPID_PUBLIC_KEY',
  'NEXT_PUBLIC_POSTHOG_KEY',
  'NEXT_PUBLIC_POSTHOG_HOST',
  'NEXT_PUBLIC_SB2MCP_ACTIONS',
  'NEXT_PUBLIC_BC_WEB_BASE_URL',
  'NEXT_PUBLIC_BC_WEB_COMPANY',
  // Server-side it is the deployment's own name (see utils/environment.ts);
  // exposed so the browser can tag Sentry/analytics without guessing from URLs.
  'APP_ENV'
] as const

export type PublicEnvKey = (typeof PUBLIC_ENV_KEYS)[number]
export type PublicEnv = Partial<Record<PublicEnvKey, string>>

/** The global `/api/public-env` assigns (the `Window` key declared below). */
const PUBLIC_ENV_GLOBAL = '__ATLAS_PUBLIC_ENV__'

declare global {
  interface Window {
    __ATLAS_PUBLIC_ENV__?: PublicEnv
  }
}

/**
 * A deploy-time value, or `undefined` when unset or empty.
 *
 * The browser reads what `/api/public-env` served; everything else (server,
 * and tests that set `process.env` under jsdom) falls through to the process
 * environment at call time.
 */
export function publicEnv(key: PublicEnvKey): string | undefined {
  if (typeof window !== 'undefined') {
    const served = window[PUBLIC_ENV_GLOBAL]?.[key]
    if (served) return served
  }
  if (typeof process === 'undefined') return undefined
  return process.env[key] || undefined
}

/** Every allowlisted key that has a value in this process's environment. */
export function readPublicEnv(): PublicEnv {
  const env: PublicEnv = {}
  for (const key of PUBLIC_ENV_KEYS) {
    const value = process.env[key]
    if (value) env[key] = value
  }
  return env
}

/** The body `/api/public-env` serves: one assignment, nothing executable else. */
export function renderPublicEnvScript(env: PublicEnv): string {
  // `<` escaped so the payload can never close a <script> it is inlined into.
  const json = JSON.stringify(env).replace(/</g, '\\u003c')
  return `window.${PUBLIC_ENV_GLOBAL}=${json};\n`
}
